/**
 * MailPage - Módulo completo de correos corporativos
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  Mail,
  Send,
  FileText,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Plus,
  Loader2,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';

export function MailPage() {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [templates, setTemplates] = useState([
    {
      id: 'welcome',
      name: 'Bienvenida',
      subject: 'Bienvenido a ZAPHUB - {{companyName}}',
      preview: 'Email de bienvenida para nuevos usuarios',
    },
    {
      id: 'weekly_report',
      name: 'Reporte Semanal',
      subject: 'Reporte Semanal - {{weekNumber}}',
      preview: 'Resumen semanal de actividad',
    },
    {
      id: 'certification_alert',
      name: 'Alerta de Certificación',
      subject: '⚠️ Certificación Requerida',
      preview: 'Notificación de certificación pendiente',
    },
    {
      id: 'password_reset',
      name: 'Recuperación de Contraseña',
      subject: 'Restablece tu contraseña - ZAPHUB',
      preview: 'Link de recuperación de contraseña',
    },
  ]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  const sentEmails = [
    {
      id: '1',
      to: ['admin@empresa.com'],
      subject: 'Bienvenido a ZAPHUB',
      status: 'sent',
      sentAt: '2024-01-15 09:30',
    },
    {
      id: '2',
      to: ['team@empresa.com'],
      subject: 'Reporte Semanal - Semana 3',
      status: 'sent',
      sentAt: '2024-01-15 09:00',
    },
    {
      id: '3',
      to: ['user@empresa.com'],
      subject: 'Certificación Requerida',
      status: 'failed',
      sentAt: '2024-01-14 16:45',
    },
  ];

  const stats = {
    total: 2450,
    sent: 2398,
    failed: 52,
    successRate: 97.9,
  };

  const handleSend = async () => {
    if (!recipients || !subject || !body) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setIsSending(true);
    toast.info('Enviando correo...');
    
    // Simular envío
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSending(false);
    toast.success(`Correo enviado correctamente a ${recipients.split(',').length} destinatario(s)`);
    
    // Limpiar formulario
    setRecipients('');
    setSubject('');
    setBody('');
    setSelectedTemplate('');
  };

  const handleSaveDraft = () => {
    toast.success('Borrador guardado correctamente');
  };

  const handleUseTemplate = (template: any) => {
    setSelectedTemplate(template.id);
    setSubject(template.subject);
    toast.success(`Template "${template.name}" cargado`);
  };

  const handleCreateTemplate = () => {
    toast.info('Abriendo editor de templates...');
    // Aquí normalmente abrirías un modal o navegarías a otra página
  };

  const handleEditTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    toast.info(`Editando template "${template?.name}"...`);
  };

  const handleDeleteTemplate = () => {
    if (templateToDelete) {
      const template = templates.find(t => t.id === templateToDelete);
      setTemplates(prev => prev.filter(t => t.id !== templateToDelete));
      toast.success(`Template "${template?.name}" eliminado`);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const openDeleteDialog = (templateId: string) => {
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-8 p-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl mb-2">Correos Corporativos</h1>
        <p className="text-muted-foreground">
          Gestión de correos electrónicos con templates y tracking
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Enviados</CardTitle>
            <Mail className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold mb-1">{stats.total.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Este mes</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Exitosos</CardTitle>
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold mb-1">{stats.sent.toLocaleString()}</div>
            <p className="text-xs text-green-600">
              <TrendingUp className="inline h-3 w-3 mr-1" />
              {stats.successRate}% tasa de éxito
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Fallidos</CardTitle>
            <XCircle className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold mb-1">{stats.failed}</div>
            <p className="text-xs text-muted-foreground">Requieren atención</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Templates</CardTitle>
            <FileText className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold mb-1">{templates.length}</div>
            <p className="text-xs text-muted-foreground">Disponibles</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="compose" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="compose">Redactar</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="sent">Enviados</TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose" className="space-y-4 mt-6">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
            <CardHeader>
              <CardTitle>Nuevo Correo</CardTitle>
              <CardDescription>
                Envía correos usando templates o crea uno personalizado
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template">Template (Opcional)</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Selecciona un template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin template</SelectItem>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipients">Destinatarios *</Label>
                <Input
                  id="recipients"
                  placeholder="usuario@empresa.com, otro@empresa.com"
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  className="h-11"
                />
                <p className="text-xs text-muted-foreground">
                  Separa múltiples direcciones con comas
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Asunto *</Label>
                <Input
                  id="subject"
                  placeholder="Asunto del correo"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="body">Mensaje *</Label>
                <Textarea
                  id="body"
                  placeholder="Contenido del correo..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                />
                <p className="text-xs text-muted-foreground">
                  Usa variables como {'{{userName}}'}, {'{{companyName}}'}, etc.
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleSend} 
                  className="bg-primary hover:bg-primary/90"
                  disabled={isSending}
                >
                  {isSending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Enviar Correo
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleSaveDraft}>
                  Guardar Borrador
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">
              {templates.length} templates disponibles
            </p>
            <Button className="bg-primary hover:bg-primary/90" onClick={handleCreateTemplate}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Template
            </Button>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {templates.map((template) => (
              <Card key={template.id} className="relative overflow-hidden border-border hover:shadow-lg transition-shadow">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1.5">{template.name}</CardTitle>
                      <CardDescription>
                        {template.preview}
                      </CardDescription>
                    </div>
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1 font-medium">Asunto:</p>
                      <p className="text-sm">{template.subject}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUseTemplate(template)}
                      >
                        Usar Template
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditTemplate(template.id)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openDeleteDialog(template.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Sent Tab */}
        <TabsContent value="sent" className="space-y-4 mt-6">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
            <CardHeader>
              <CardTitle>Historial de Envíos</CardTitle>
              <CardDescription>
                Últimos correos enviados desde la plataforma
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sentEmails.map((email) => (
                  <div
                    key={email.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                        email.status === 'sent'
                          ? 'bg-green-500/10 text-green-600'
                          : 'bg-red-500/10 text-red-600'
                      }`}>
                        {email.status === 'sent' ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <XCircle className="h-5 w-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{email.subject}</p>
                        <p className="text-xs text-muted-foreground">
                          Para: {email.to.join(', ')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={email.status === 'sent' ? 'default' : 'destructive'}
                        className="text-xs"
                      >
                        {email.status === 'sent' ? 'Enviado' : 'Fallido'}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 justify-end">
                        <Clock className="h-3 w-3" />
                        {email.sentAt}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Template</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar este template? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={handleDeleteTemplate}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
